import { getDesignerEdgeOrientation } from '../design/designer-commands';
import {
  applySetbacks,
  computeSegmentSetbacks
} from '../setbacks/setback-transitions';

const EDGE_LABEL_PRIORITY = ['Eave', 'Ridge', 'Valley'];

export function calculateAzimuthFromEdgeLabels(segment) {
  const { lines, geometry } = segment || {};
  if (!Array.isArray(lines) || !Array.isArray(geometry) || !geometry.length) {
    return null;
  }

  const bestEdges = EDGE_LABEL_PRIORITY.reduce((result, label) => {
    result[label] = { index: -1, length: 0 };
    return result;
  }, {});

  geometry.forEach((point, index) => {
    const label = lines[index];
    if (!bestEdges[label]) return;
    const nextPoint = geometry[(index + 1) % geometry.length];
    if (!Array.isArray(point) || !Array.isArray(nextPoint)) return;
    const length = Math.hypot(point[0] - nextPoint[0], point[1] - nextPoint[1]);
    if (bestEdges[label].length < length) {
      bestEdges[label] = { index, length };
    }
  });

  const selectedLabel = EDGE_LABEL_PRIORITY.find(
    (label) => bestEdges[label].index >= 0
  );
  if (!selectedLabel) return null;

  const edgeIndex = bestEdges[selectedLabel].index;
  const point1 = geometry[edgeIndex];
  const point2 = geometry[(edgeIndex + 1) % geometry.length];
  let dx = point2[0] - point1[0];
  let dy = -(point2[1] - point1[1]);
  if (getDesignerEdgeOrientation(geometry, edgeIndex) === 'ccw') {
    dx = -dx;
    dy = -dy;
  }
  const theta =
    selectedLabel === 'Eave'
      ? (Math.atan2(-dy, dx) * 180) / Math.PI
      : (Math.atan2(dy, -dx) * 180) / Math.PI;
  return Math.round((((theta + 360) % 360) + Number.EPSILON) * 100) / 100;
}

export function applyAzimuthFromEdgeLabels(state, indexes = []) {
  if (!state?.automaticAzimuth || !Array.isArray(state.segments)) return state;
  const indexSet = new Set(
    (Array.isArray(indexes) ? indexes : []).map(Number).filter(Number.isInteger)
  );
  if (!indexSet.size) return state;

  let changed = false;
  const segments = state.segments.map((segment, index) => {
    if (!indexSet.has(index)) return segment;
    const azimuth = calculateAzimuthFromEdgeLabels(segment);
    if (azimuth === null || segment.azimuth === azimuth) return segment;
    changed = true;
    return { ...segment, azimuth };
  });
  return changed ? { ...state, segments } : state;
}

export function applyDesignerRoofEffects(state, effects = []) {
  let next = state;
  const azimuthIndexes = (effects || [])
    .filter((item) => item?.type === 'UPDATE_AZIMUTH')
    .flatMap((item) => item.indexes || []);
  if (azimuthIndexes.length) {
    next = applyAzimuthFromEdgeLabels(next, azimuthIndexes);
  }
  if ((effects || []).some((item) => item?.type === 'UPDATE_SETBACKS')) {
    const setbacks = applySetbacks(next, { forceUpdate: true });
    next = { ...setbacks };
    delete next.__setbacksChanged;
  }
  return next;
}

export function transitionSegmentEdgeTypes(state, changedSegments = []) {
  const segments = (state.segments || []).map((segment, index) => {
    const changedSegment = changedSegments?.[index] || {};
    const geometrySource = Array.isArray(changedSegment.geometry)
      ? changedSegment.geometry
      : segment.geometry;
    const geometry = Array.isArray(geometrySource)
      ? geometrySource.map((point) =>
          Array.isArray(point) ? [...point] : point
        )
      : [];
    const changedLines = changedSegment.lines;
    const geometryLength =
      geometry.length || changedLines?.length || segment?.lines?.length || 0;
    const lines = Array.isArray(changedLines)
      ? [
          ...changedLines.slice(0, geometryLength),
          ...Array(Math.max(0, geometryLength - changedLines.length)).fill('')
        ]
      : Array.isArray(segment.lines)
        ? [...segment.lines]
        : Array(geometryLength).fill('');
    const nextSegment = {
      ...segment,
      ...changedSegment,
      geometry,
      lines
    };

    if (!state.automaticAzimuth) {
      nextSegment.azimuth = segment.azimuth;
    } else {
      const azimuth = calculateAzimuthFromEdgeLabels(nextSegment);
      if (azimuth !== null) nextSegment.azimuth = azimuth;
    }

    return computeSegmentSetbacks(
      nextSegment,
      state.fireSetbacks,
      state.setbackMode,
      { forceUpdate: true }
    ).segment;
  });

  return { ...state, segments };
}
